import { FormPath, isField, type Form } from "@formily/core";
import { useField, useForm } from "@formily/react";
import { batch } from "@formily/reactive";
import { useEffect } from "react";

/**
 * Formily drains a plain batch's reactions recursively, so a form-wide mutation nests once per
 * field and overflows the stack on an editable table. A scoped batch drains in a loop instead.
 */
export const scopedBatch = <T>(callback: () => T): T => batch.scope!(callback);

/** Formily rejects form.submit() with its undocumented errors array, which is duck-typed here. */
export function isValidationError(e: unknown) {
  const first = Array.isArray(e) ? e[0] : null;
  return first != null && typeof first === "object" && "code" in first && "messages" in first;
}

/**
 * This isn't particularly flexible, but used in editable tables
 */
export const getModifiedTopLevelValues = <T extends object>(values: T, form: Form<T>): T => {
  return Object.fromEntries(Object.entries(values).filter(([key]) => form.getFieldState(key).modified)) as T;
};

export const useInitialValueSync = (name: string | number | RegExp, initialValue: unknown) => {
  const form = useForm();
  const parent = useField();
  const address = FormPath.parse(parent.address).concat(name);
  useEffect(() => {
    form.query(address).forEach((field) => {
      if (!isField(field)) return;
      if (field.initialValue !== initialValue) {
        field.setInitialValue(initialValue);
      }
    });
  }, [form, initialValue, address]);
};
